/**
 * The governed model-template registry (`/model-templates`), for the
 * template-governance screen (UI-04).
 *
 * `/model-change/adopt` creates the next template version as a DRAFT and tells
 * the reviewer to "complete the governed chain (submit → approve → lock)";
 * until this module nothing in the browser called the chain, so adopted
 * models stayed drafts and their figures were never extracted. Every route
 * here is RBAC-gated on `underwriting_models` on the server and a denied
 * attempt is itself audited there; this module only carries the calls.
 */
import api from '@/services/api'
import { toCamelCase } from '@/utils/transform'

export type TemplateStatus = 'draft' | 'pending_approval' | 'approved' | 'locked' | 'retired'

export interface ModelTemplate {
  id: string
  name: string
  client: string
  version: number
  status: TemplateStatus | string
  workbookSha256: string
  createdBy: string
  approvedBy: string | null
  anchorMap: { anchors?: { metric: string; sheet: string; labelCell: string; valueCell: string; expectLabel?: string }[] } & Record<string, unknown>
}

export interface ModelTemplateEvent {
  seq: number
  templateId: string | null
  /** CREATE | UPDATE | SUBMIT | APPROVE | LOCK | RETIRE | ACCESS_DENIED */
  type: string
  actor: string
  detail: Record<string, unknown> | null
  at: string | null
  hash: string
}

/** A mapgen proposal stored on an intake record for a workbook the engine has
 *  not been onboarded for — the start of a template, waiting on a person. */
export interface OnboardingDraft {
  ingestionId: string
  filename: string
  subject: string | null
  receivedAt: string | null
  model: string | null
  /** Metrics the sweep found a candidate anchor for, and the candidate cells in all. */
  metrics: number
  candidates: number
}

export type ApprovalMode = 'dual' | 'single'

export interface TemplateGovernance {
  /** The platform setting: `dual` is four-eyes (the author may not approve their own version). */
  approvalMode: ApprovalMode
  onboardingDrafts: OnboardingDraft[]
  scannedRecords: number
  scanLimit: number
}

export const modelTemplateKeys = {
  all: ['model-templates'] as const,
  list: ['model-templates', 'list'] as const,
  events: ['model-templates', 'events'] as const,
  chain: ['model-templates', 'chain'] as const,
  governance: ['model-templates', 'governance'] as const,
}

export const modelTemplatesService = {
  list: () => api.get('/model-templates/').then((r) => toCamelCase<ModelTemplate[]>(r.data)),
  events: () => api.get('/model-templates/events').then((r) => toCamelCase<ModelTemplateEvent[]>(r.data)),
  verifyChain: () =>
    api.get('/model-templates/chain/verify').then((r) => toCamelCase<{ ok: boolean; firstBrokenSeq: number | null }>(r.data)),
  governance: () => api.get('/model-templates/governance').then((r) => toCamelCase<TemplateGovernance>(r.data)),
  submit: (id: string) => api.post(`/model-templates/${id}/submit`).then((r) => toCamelCase<ModelTemplate>(r.data)),
  approve: (id: string, basis: string) =>
    api.post(`/model-templates/${id}/approve`, { basis }).then((r) => toCamelCase<ModelTemplate>(r.data)),
  lock: (id: string) => api.post(`/model-templates/${id}/lock`).then((r) => toCamelCase<ModelTemplate>(r.data)),
  retire: (id: string) => api.post(`/model-templates/${id}/retire`).then((r) => toCamelCase<ModelTemplate>(r.data)),
}

/** The one action the chain offers a version in its current status. */
export function nextStep(status: string): { verb: 'submit' | 'approve' | 'lock'; label: string } | null {
  switch (status) {
    case 'draft':
      return { verb: 'submit', label: 'Submit for approval' }
    case 'pending_approval':
      return { verb: 'approve', label: 'Approve' }
    case 'approved':
      return { verb: 'lock', label: 'Lock' }
    default:
      return null
  }
}
